import React, { useState, useEffect } from "react";
import { useBackfillBetsMutation } from "@/api/betting";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertTriangle,
  Loader2,
  Database,
  Calendar,
  Filter,
  UserRound,
  ChevronsUpDown,
  Check,
} from "lucide-react";
import { cn } from "@/lib/utils";

// Define persona type
interface Persona {
  persona_id: string;
  name: string;
  [key: string]: unknown;
}

// Define error response type
interface ErrorResponse {
  response?: {
    data?: {
      detail?: string;
    };
  };
  message?: string;
}

interface BackfillModalProps {
  isOpen: boolean;
  onClose: () => void;
  personas: Persona[];
  initialPersonaId?: string;
}

const BackfillModal: React.FC<BackfillModalProps> = ({
  isOpen,
  onClose,
  personas,
  initialPersonaId = "",
}) => {
  const { toast } = useToast();
  const backfillBets = useBackfillBetsMutation();
  const [personaOpen, setPersonaOpen] = useState(false);
  const [selectedPersonaId, setSelectedPersonaId] =
    useState<string>(initialPersonaId);
  const [daysBack, setDaysBack] = useState(14);
  const [batchSize, setBatchSize] = useState(10);
  const [limit, setLimit] = useState(200);
  const [runValidation, setRunValidation] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update selected persona when initialPersonaId changes
  useEffect(() => {
    setSelectedPersonaId(initialPersonaId);
  }, [initialPersonaId]);

  // Reset state when modal is closed
  useEffect(() => {
    if (!isOpen) {
      // Keep the selected persona, but reset the open state
      setPersonaOpen(false);
    }
  }, [isOpen]);

  const handlePersonaSelect = (selectedValue: string) => {
    console.log("Selected persona ID:", selectedValue);
    setSelectedPersonaId(selectedValue);
    setPersonaOpen(false);
  };

  const handleBackfill = () => {
    if (!selectedPersonaId) {
      toast({
        variant: "destructive",
        description:
          "Please select an agent before starting the backfill process.",
      });
      return;
    }

    setIsSubmitting(true);

    // Ensure all numeric values are valid
    const validDaysBack = Math.max(1, Math.min(90, daysBack || 14));
    const validBatchSize = Math.max(1, Math.min(50, batchSize || 10));
    const validLimit = Math.max(10, Math.min(1000, limit || 200));

    // Construct request with validated values
    const request = {
      persona_id: selectedPersonaId,
      days_back: validDaysBack,
      batch_size: validBatchSize,
      limit: validLimit,
      validate: runValidation,
    };

    console.log("Submitting backfill request:", request);

    backfillBets.mutate(request, {
      onSuccess: (response) => {
        toast({
          description: `Backfill job started: ${response.job_id}`,
        });
        setIsSubmitting(false);
        onClose();
      },
      onError: (error: ErrorResponse) => {
        // Try to extract more detailed error information
        const errorMsg =
          error.response?.data?.detail || error.message || "Unknown error";

        toast({
          variant: "destructive",
          description: `Error starting backfill job: ${errorMsg}`,
        });
        setIsSubmitting(false);
      },
    });
  };

  // Find selected persona object
  const selectedPersona = personas.find(
    (p) => p.persona_id === selectedPersonaId,
  );
  const selectedPersonaName = selectedPersona
    ? selectedPersona.name
    : "Choose an agent";

  // Handle custom close to ensure proper cleanup
  const handleDialogClose = () => {
    // Make sure popover is closed
    setPersonaOpen(false);
    // Call the provided onClose function
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogClose}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Database className="h-5 w-5 text-amber-400" />
            Backfill Betting Data
          </DialogTitle>
          <DialogDescription>
            Extract and validate betting predictions from historical messages.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-5">
          {/* Agent Selection */}
          <div className="bg-black/30 border border-zinc-800 rounded-lg p-4">
            <h3 className="font-medium mb-2 flex items-center gap-2">
              <UserRound className="h-4 w-4 text-amber-400" />
              Agent Selection
            </h3>
            <div className="space-y-2">
              <Label
                htmlFor="persona-selector"
                className="text-sm text-zinc-400"
              >
                Select Agent
              </Label>
              <Popover open={personaOpen} onOpenChange={setPersonaOpen}>
                <PopoverTrigger asChild>
                  <Button
                    id="persona-selector"
                    variant="outline"
                    role="combobox"
                    aria-expanded={personaOpen}
                    className="w-full justify-between bg-black/20 border-zinc-700 h-9"
                  >
                    {selectedPersonaId ? (
                      <span className="truncate max-w-[180px]">
                        {selectedPersonaName}
                      </span>
                    ) : (
                      <span className="text-zinc-500">Choose an agent</span>
                    )}
                    <ChevronsUpDown className="h-4 w-4 text-zinc-500 ml-2 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 bg-zinc-900 border-zinc-700">
                  <Command className="bg-transparent">
                    <CommandInput
                      placeholder="Search agent..."
                      className="border-zinc-700"
                    />
                    <CommandList className="thin-scrollbar">
                      <CommandEmpty>No agent found.</CommandEmpty>
                      <CommandGroup>
                        {personas
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((persona) => (
                            <CommandItem
                              key={persona.persona_id}
                              onSelect={() =>
                                handlePersonaSelect(persona.persona_id)
                              }
                              className="hover:bg-zinc-800"
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  selectedPersonaId === persona.persona_id
                                    ? "opacity-100"
                                    : "opacity-0",
                                )}
                              />
                              {persona.name}
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="text-xs text-zinc-500">
                Select the agent whose messages will be processed for betting
                predictions
              </p>
            </div>
          </div>

          {/* Time Period */}
          <div className="bg-black/30 border border-zinc-800 rounded-lg p-4">
            <h3 className="font-medium mb-2 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-amber-400" />
              Time Period
            </h3>
            <div className="space-y-2">
              <Label htmlFor="days-back" className="text-sm text-zinc-400">
                Days to Look Back
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="days-back"
                  type="number"
                  min={1}
                  max={90}
                  value={daysBack}
                  onChange={(e) => setDaysBack(parseInt(e.target.value) || 14)}
                  className="bg-black/20 border-zinc-700"
                />
                <span className="text-sm text-zinc-400">days</span>
              </div>
              <p className="text-xs text-zinc-500">
                Messages from the last {daysBack} days will be analyzed
              </p>
            </div>
          </div>

          {/* Validation Option */}
          <div className="bg-black/30 border border-zinc-800 rounded-lg p-4">
            <h3 className="font-medium mb-3 flex items-center gap-2">
              <Check className="h-4 w-4 text-amber-400" />
              Validation Options
            </h3>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm">Validate extracted bets</p>
                <p className="text-xs text-zinc-500">
                  Automatically verify betting results after extraction
                </p>
              </div>
              <Switch
                id="run-validation"
                checked={runValidation}
                onCheckedChange={setRunValidation}
              />
            </div>
          </div>

          {/* Advanced Options */}
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="advanced" className="border-zinc-800">
              <AccordionTrigger className="py-3 px-4 bg-black/30 border border-zinc-800 rounded-lg hover:no-underline hover:bg-black/40">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Filter className="h-4 w-4 text-zinc-400" />
                  <span>Advanced Options</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-3">
                <div className="space-y-4 px-4 pb-2">
                  <div className="space-y-2">
                    <Label
                      htmlFor="message-limit"
                      className="text-sm text-zinc-400"
                    >
                      Message Limit
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="message-limit"
                        type="number"
                        min={10}
                        max={1000}
                        value={limit}
                        onChange={(e) =>
                          setLimit(parseInt(e.target.value) || 200)
                        }
                        className="bg-black/20 border-zinc-700"
                      />
                      <span className="text-sm text-zinc-400">messages</span>
                    </div>
                    <p className="text-xs text-zinc-500">
                      Maximum number of messages to process
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="batch-size"
                      className="text-sm text-zinc-400"
                    >
                      Batch Size
                    </Label>
                    <Input
                      id="batch-size"
                      type="number"
                      min={1}
                      max={50}
                      value={batchSize}
                      onChange={(e) =>
                        setBatchSize(parseInt(e.target.value) || 10)
                      }
                      className="bg-black/20 border-zinc-700"
                    />
                    <p className="text-xs text-zinc-500">
                      Number of messages to process in each batch
                    </p>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <Alert className="bg-amber-950/20 border-amber-800">
            <AlertTriangle className="h-4 w-4 text-amber-400" />
            <AlertTitle className="text-amber-400">Backfill Process</AlertTitle>
            <AlertDescription className="text-xs text-zinc-300">
              This process will:
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>Scan past messages for betting predictions</li>
                <li>Extract structured bet data for tracking</li>
                {runValidation && (
                  <li>Validate bet outcomes using sports data sources</li>
                )}
                <li>Update the betting database with results</li>
              </ul>
              <div className="mt-2">
                The process runs in the background and may take several minutes
                to complete.
              </div>
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleBackfill}
            disabled={isSubmitting || !selectedPersonaId}
            className="bg-amber-600 hover:bg-amber-700"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Starting...
              </>
            ) : (
              "Start Backfill"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BackfillModal;
